import {
  SET_APPOINTMENT_TYPE,
  SET_SHOP,
  SET_PARTNER,
  SET_DATE_SLOT,
  SET_TIME_SLOT,
  SET_DRAFT_APPOINTMENT,
  SET_ADDRESS,
  SET_EMPTY_ADDRESS,
  SET_ADDRESS_DESCRIPTION,
  SET_EMPTY_ADDRESS_DESCRIPTION,
  SET_RELATED_APPOINTMENT_DRAFT,
  UPDATE_APPOINTMENT_TYPE,
  SET_PARTNERS_GROUP_ID,
  SET_RELATED_APPOINTMENT_TIME_SLOT,
  SET_PRODUCTS,
  SET_FITTING_TYPES,
  SET_PIERCING_TYPE,
  SELECTED_SERVICE_TYPE_CHANGED,
} from './appointment-draft.actions';
import { SET_APPOINTMENT } from './appointment.actions';

import { isEmpty, replaceItem } from '~/utils/utils';

const initialDraft = {
  appointmentType: {
    id: '',
  },
  shop: { id: '' },
  partner: { id: '' },
  dateSlot: '',
  timeSlot: { from: 0, eventId: '' },
  address: null,
  addressDescription: null,
  partnersGroupId: '',
  addons: [],
  fittingTypes: null,
  piercingType: null,
  selectedServiceTypeChanged: false,
};

const initialRelatedAppointmentDraft = {
  appointmentType: {
    id: '',
  },
  dateSlot: '',
  timeSlot: { from: 0, eventId: '' },
};

const emptyAddress = {
  city: '',
  county: '',
  postcode: '',
  street1: '',
  street2: '',
  street3: '',
  street4: '',
};

const emptyAddressDescription = {
  id: '',
  description: '',
};

const initialState = {
  draft: initialDraft,
  appointment: null,
};

export const appointmentReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case SET_APPOINTMENT_TYPE:
      return {
        ...state,
        draft: {
          ...initialDraft,
          appointmentType: payload || initialDraft.appointmentType,
        },
      };
    case UPDATE_APPOINTMENT_TYPE: {
      const isAppointmentTypeHasSelectedPartnersGroup =
        payload &&
        payload.groups &&
        payload.groups.some((group) => group.id === state.draft.partnersGroupId);
      return {
        ...state,
        draft: {
          ...state.draft,
          appointmentType: payload || initialDraft.appointmentType,
          address: state.draft.address || initialDraft.address,
          addressDescription: state.draft.addressDescription || initialDraft.addressDescription,
          partnersGroupId: isAppointmentTypeHasSelectedPartnersGroup
            ? state.draft.partnersGroupId
            : initialDraft.partnersGroupId,
          addons: state.draft.addons.map((addon) => ({
            ...addon,
            // reset only 'type' of related appointment type, because we want to keep dateSlot and timeSlot selection
            appointmentType: initialRelatedAppointmentDraft.appointmentType,
          })),
        },
      };
    }
    case SET_SHOP: {
      const selectedShopId = state.draft.shop.id;
      let selectedPartner;
      if (payload) {
        selectedPartner =
          selectedShopId !== payload.id
            ? {
                partner: initialDraft.partner,
              }
            : {};
      }

      return {
        ...state,
        draft: {
          ...state.draft,
          shop: payload || initialDraft.shop,
          ...selectedPartner,
        },
      };
    }
    case SET_PARTNERS_GROUP_ID: {
      const selectedPartnersGroupId = state.draft.partnersGroupId;
      let selectedPartner;
      if (payload) {
        selectedPartner =
          selectedPartnersGroupId !== payload
            ? {
                partner: initialDraft.partner,
              }
            : {};
      }

      return {
        ...state,
        draft: {
          ...state.draft,
          partnersGroupId: payload || initialDraft.partnersGroupId,
          ...selectedPartner,
        },
      };
    }
    case SET_PARTNER:
      return {
        ...state,
        draft: {
          ...state.draft,
          partner: payload || initialDraft.partner,
        },
      };
    case SET_DATE_SLOT:
      return {
        ...state,
        draft: {
          ...state.draft,
          addons: state.draft.addons.map((addon) => ({
            ...addon,
            dateSlot: payload || initialRelatedAppointmentDraft.dateSlot,
            timeSlot: initialRelatedAppointmentDraft.timeSlot,
          })),
          dateSlot: payload || initialDraft.dateSlot,
          timeSlot: initialDraft.timeSlot,
        },
      };
    case SET_TIME_SLOT:
      return {
        ...state,
        draft: {
          ...state.draft,
          timeSlot: payload || initialDraft.timeSlot,
        },
      };
    case SET_RELATED_APPOINTMENT_TIME_SLOT: {
      const {
        draft: { addons },
      } = state;

      const { appointmentTypeId, ...selectedTimeSlotData } = payload;

      const addonIndexToBeUpdated = addons.findIndex(
        (addon) => addon.appointmentType.id === appointmentTypeId,
      );

      if (addonIndexToBeUpdated !== -1) {
        return {
          ...state,
          draft: {
            ...state.draft,
            addons: replaceItem(addons, addonIndexToBeUpdated, {
              timeSlot: isEmpty(selectedTimeSlotData)
                ? initialRelatedAppointmentDraft.timeSlot
                : { ...selectedTimeSlotData, appointmentTypeId },
            }),
          },
        };
      }
      return state;
    }
    case SET_ADDRESS:
      return {
        ...state,
        draft: {
          ...state.draft,
          address: payload || initialDraft.address,
        },
      };
    case SET_EMPTY_ADDRESS:
      return {
        ...state,
        draft: {
          ...state.draft,
          address: emptyAddress,
        },
      };
    case SET_ADDRESS_DESCRIPTION:
      return {
        ...state,
        draft: {
          ...state.draft,
          addressDescription: payload || initialDraft.addressDescription,
        },
      };
    case SET_EMPTY_ADDRESS_DESCRIPTION:
      return {
        ...state,
        draft: {
          ...state.draft,
          addressDescription: emptyAddressDescription,
        },
      };
    case SET_DRAFT_APPOINTMENT:
      return {
        ...state,
        draft: payload,
      };
    case SET_APPOINTMENT:
      return {
        ...state,
        appointment: payload,
      };
    case SET_RELATED_APPOINTMENT_DRAFT: {
      const {
        draft: { addons },
      } = state;

      if (!addons.length) {
        return {
          ...state,
          draft: {
            ...state.draft,
            addons: [
              {
                ...initialRelatedAppointmentDraft,
                appointmentType: payload,
              },
            ],
          },
        };
      }

      const addon = state.draft.addons[0]; // assume that only one related treatment can be selected
      return {
        ...state,
        draft: {
          ...state.draft,
          addons: [
            {
              ...addon,
              appointmentType: payload,
              timeSlot: {
                ...addon.timeSlot,
                appointmentTypeId: payload.id,
              },
            },
          ],
        },
      };
    }
    case SET_PRODUCTS:
      return {
        ...state,
        draft: {
          ...state.draft,
          products: payload,
        },
      };

    case SET_FITTING_TYPES:
      return {
        ...state,
        draft: {
          ...state.draft,
          fittingTypes: payload,
        },
      };
    case SET_PIERCING_TYPE:
      return {
        ...state,
        draft: {
          ...state.draft,
          piercingType: payload,
        },
      };
    case SELECTED_SERVICE_TYPE_CHANGED:
      return {
        ...state,
        draft: {
          ...state.draft,
          selectedServiceTypeChanged: payload,
        },
      };
    default:
      return state;
  }
};
